/**
 * ImageGallery Component
 * Display company gallery images with lightbox
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

interface Image {
  id: string;
  imageUrl: string;
  caption?: string;
  altText?: string;
  displayOrder: number;
}

interface ImageGalleryProps {
  images: Image[];
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Sort images by display order
  const sortedImages = [...images].sort((a, b) => a.displayOrder - b.displayOrder);

  const openLightbox = (index: number) => {
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
  };

  const goToPrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedIndex !== null && selectedIndex < sortedImages.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;

      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, sortedImages.length]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (selectedIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedIndex]);

  if (!sortedImages || sortedImages.length === 0) {
    return null;
  }

  return (
    <>
      {/* Gallery Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {sortedImages.map((image, index) => (
          <button
            key={image.id}
            onClick={() => openLightbox(index)}
            className="relative aspect-video bg-gray-200 rounded-lg overflow-hidden
                       hover:opacity-90 transition-opacity group focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <img
              src={image.imageUrl}
              alt={image.altText || `Gallery image ${index + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />

            {/* Caption Overlay */}
            {image.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t
                            from-black/70 to-transparent p-3 opacity-0
                            group-hover:opacity-100 transition-opacity">
                <p className="text-white text-sm font-medium line-clamp-2">
                  {image.caption}
                </p>
              </div>
            )}

            {/* Image Number Badge */}
            <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md">
              {index + 1}/{sortedImages.length}
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors z-10"
              title="ปิด (ESC)"
            >
              <FiX className="w-8 h-8" />
            </button>

            {/* Previous Button */}
            {selectedIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white hover:bg-white/10 rounded-full transition-colors z-10"
                title="รูปก่อนหน้า (←)"
              >
                <FiChevronLeft className="w-8 h-8" />
              </button>
            )}

            {/* Image Container */}
            <motion.div
              key={selectedIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="max-w-7xl max-h-[90vh] mx-auto flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image */}
              <img
                src={sortedImages[selectedIndex].imageUrl}
                alt={sortedImages[selectedIndex].altText || ''}
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              />

              {/* Caption */}
              {sortedImages[selectedIndex].caption && (
                <p className="text-white text-center mt-4 text-lg font-medium px-4">
                  {sortedImages[selectedIndex].caption}
                </p>
              )}

              {/* Counter */}
              <p className="text-white/70 text-center mt-2 text-sm">
                {selectedIndex + 1} / {sortedImages.length}
              </p>
            </motion.div>

            {/* Next Button */}
            {selectedIndex < sortedImages.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white hover:bg-white/10 rounded-full transition-colors z-10"
                title="รูปถัดไป (→)"
              >
                <FiChevronRight className="w-8 h-8" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageGallery;
